import { Image, Typography } from "antd";
import {
  Calendar,
  ChevronDown,
  ExternalLink,
  MapPin,
  Package,
  Printer,
  Ruler,
  Scale,
  Truck,
  User,
  Users,
  Wrench,
  X,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TBooking, TInventory, TServiceTicket } from "~/types";
import { capitalize, formatDate, getServiceDisplayTime } from "~/utils";
import { JobStatus, ServiceType } from "~/utils/constants";
import { ServiceCompletionSidebar } from "./service-completion-sidebar";
import { PrintWaybill } from "~/components/admin/dispatch/print-labels/print-waybill";
import { TWaybillService } from "~/types/waybill-service";

const { Text, Title } = Typography;

// Expandable inventory item component
const InventoryExpandableItem: React.FC<{ item: TInventory; onViewInInventory: (item: TInventory) => void }> = ({ item, onViewInInventory }) => {
  const [expanded, setExpanded] = useState(false);

  const hasDimensions = item.length || item.width || item.height;
  const dimensionsDisplay = hasDimensions
    ? `${item.length || 0}" × ${item.width || 0}" × ${item.height || 0}"`
    : null;

  const hasImages = item.images && item.images.length > 0;

  return (
    <div className="border-2 border-black bg-[#fffff5] overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-start justify-between hover:bg-[#fffef5] transition-colors text-left"
      >
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Package size={16} className="text-black flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            {/* Title - bold, on top */}
            {item.title && (
              <Text strong className="block text-sm mb-1 text-black">
                {item.title}
              </Text>
            )}
            {/* Artist - below title */}
            {item.artistName && (
              <Text className="text-xs block mb-1 text-black">
                by {item.artistName}
              </Text>
            )}
            {/* Item # and Client Reference # */}
            <div className="space-y-0.5">
              {item.inventoryRef && (
                <Text className="text-xs text-black">
                  Item #: {item.inventoryRef}
                </Text>
              )}
              {item.clientInternalRef && (
                <Text className="text-xs text-black">
                  Client Ref #: {item.clientInternalRef}
                </Text>
              )}
            </div>
          </div>
        </div>
        {/* Dimensions and Weight on the right */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
          {dimensionsDisplay && (
            <div className="flex items-center gap-1">
              <Ruler size={12} className="text-black" />
              <Text className="text-xs whitespace-nowrap text-black">
                {dimensionsDisplay}
              </Text>
            </div>
          )}
          {item.weight > 0 && (
            <div className="flex items-center gap-1">
              <Scale size={12} className="text-black" />
              <Text className="text-xs whitespace-nowrap text-black">
                {item.weight} lbs
              </Text>
            </div>
          )}
          <ChevronDown
            size={16}
            className={`text-black transition-transform flex-shrink-0 mt-1 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 border-t-2 border-black bg-[#fffff5]">
          {/* Images */}
          {hasImages && (
            <div className="pt-3 pb-2">
              <div className="flex gap-2 overflow-x-auto">
                <Image.PreviewGroup>
                  {item.images.slice(0, 4).map((img, idx) => (
                    <Image
                      key={idx}
                      src={img.url}
                      alt={item.title || "Inventory image"}
                      width={80}
                      height={80}
                      className="object-cover border-2 border-black"
                      style={{ objectFit: "cover" }}
                    />
                  ))}
                </Image.PreviewGroup>
                {item.images.length > 4 && (
                  <div className="w-20 h-20 bg-[#fffff5] border-2 border-black flex items-center justify-center text-black text-sm flex-shrink-0">
                    +{item.images.length - 4}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Details grid */}
          <div className="pt-2 space-y-2">
            {item.title && (
              <div className="flex justify-between text-sm">
                <Text className="text-black font-bold text-xs uppercase">Title</Text>
                <Text className="text-right max-w-[60%] truncate text-black">{item.title}</Text>
              </div>
            )}

            {item.clientInternalRef && (
              <div className="flex justify-between text-sm">
                <Text className="text-black font-bold text-xs uppercase">Client Ref</Text>
                <Text className="text-right max-w-[60%] truncate text-black">{item.clientInternalRef}</Text>
              </div>
            )}

            {item.medium && (
              <div className="flex justify-between text-sm">
                <Text className="text-black font-bold text-xs uppercase">Medium</Text>
                <Text className="text-right max-w-[60%] truncate text-black">{item.medium}</Text>
              </div>
            )}

            {dimensionsDisplay && (
              <div className="flex justify-between text-sm">
                <Text className="flex items-center gap-1 text-black font-bold text-xs uppercase">
                  <Ruler size={12} /> Dimensions
                </Text>
                <Text className="text-black">{dimensionsDisplay}</Text>
              </div>
            )}

            {item.weight > 0 && (
              <div className="flex justify-between text-sm">
                <Text className="flex items-center gap-1 text-black font-bold text-xs uppercase">
                  <Scale size={12} /> Weight
                </Text>
                <Text className="text-black">{item.weight} lbs</Text>
              </div>
            )}

            {item.value && item.value > 0 && (
              <div className="flex justify-between text-sm">
                <Text className="text-black font-bold text-xs uppercase">Value</Text>
                <Text className="text-black">${item.value.toLocaleString()}</Text>
              </div>
            )}

            {item.packingType && (
              <div className="flex justify-between text-sm">
                <Text className="text-black font-bold text-xs uppercase">Packing</Text>
                <span className="inline-block px-2 py-0.5 text-xs font-bold uppercase border-2 border-black bg-[#fffff5] text-black">
                  {capitalize(item.packingType)}
                </span>
              </div>
            )}

            {item.specialHandling && item.specialHandling.length > 0 && (
              <div className="text-sm">
                <Text className="block mb-1 text-black font-bold text-xs uppercase">Special Handling</Text>
                <div className="flex flex-wrap gap-1">
                  {item.specialHandling.map((handling, idx) => (
                    <span key={idx} className="inline-block px-2 py-0.5 text-xs font-bold uppercase border-2 border-black bg-[#fffff5] text-black">
                      {handling}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {item.description && (
              <div className="text-sm pt-1">
                <Text className="block mb-1 text-black font-bold text-xs uppercase">Description</Text>
                <Text className="text-xs text-black">{item.description}</Text>
              </div>
            )}

            {item.notes && (
              <div className="text-sm pt-1">
                <Text className="block mb-1 text-black font-bold text-xs uppercase">Notes</Text>
                <Text className="text-xs text-black">{item.notes}</Text>
              </div>
            )}

            {/* View in Inventory button */}
            <div className="pt-3 border-t-2 border-black mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewInInventory(item);
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-black bg-[#fffff5] text-black hover:bg-[#fffef5] transition-all text-sm font-bold uppercase tracking-wide"
              >
                <ExternalLink size={14} />
                View in Inventory
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ServiceDetailSidebarProps {
  service: TServiceTicket | null;
  booking: TBooking | null;
  onClose: () => void;
}

const getServiceTypeIcon = (type: ServiceType) => {
  switch (type) {
    case ServiceType.DELIVERY:
      return <Truck size={18} />;
    case ServiceType.COLLECTION:
      return <Package size={18} />;
    default:
      return <Calendar size={18} />;
  }
};

const getStatusBadge = (status: JobStatus) => {
  // In Progress is special - just italic black text, no box
  if (status === JobStatus.IN_PROGRESS) {
    return (
      <span className="text-black italic font-bold text-xs uppercase">
        In Progress
      </span>
    );
  }
  
  // Scheduled is special - just black text, no box, no italics
  if (status === JobStatus.SCHEDULED) {
    return (
      <span className="text-black font-bold text-xs uppercase">
        Scheduled
      </span>
    );
  }
  
  const statusConfig = {
    [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-black", extraClass: "" },
    [JobStatus.COMPLETED]: { text: "Completed", className: "bg-black text-white", extraClass: "" },
    [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-black", extraClass: "" },
    [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-black", extraClass: "" },
  };
  const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-black", extraClass: "" };
  return (
    <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black ${config.className}`}>
      {config.text}
    </span>
  );
};

export const ServiceDetailSidebar: React.FC<ServiceDetailSidebarProps> = ({
  service,
  booking,
  onClose,
}) => {
  const navigate = useNavigate();
  const [showCompletionDetails, setShowCompletionDetails] = useState(false);

  const handleViewInInventory = (inventory: TInventory) => {
    // Use inventory ref or title for search to ensure the item appears in results
    const searchTerm = inventory.inventoryRef || inventory.title || "";
    const params = new URLSearchParams();
    params.set("item", inventory.id);
    if (searchTerm) {
      params.set("search", searchTerm);
    }
    navigate(`/inventory?${params.toString()}`);
  };

  if (!service) return null;

  const locationDisplay = service.location
    ? `${service.location.name || ""}${service.location.name && service.location.city ? " - " : ""}${service.location.city || ""}${service.location.city && service.location.state ? ", " : ""}${service.location.state || ""}`.trim() || "-"
    : "-";

  const addressDisplay = service.location
    ? `${service.location.address || ""}${service.location.address && service.location.city ? ", " : ""}${service.location.city || ""} ${service.location.state || ""} ${service.location.zip || ""}`.trim() || "-"
    : "-";

  return (
    <div className="h-full flex relative overflow-visible">
      <div className="h-full flex flex-col bg-[#fffff5] border-l-2 border-black flex-1 overflow-visible">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b-2 border-black bg-[#fffff5]">
        <div className="flex items-center gap-2">
          {getServiceTypeIcon(service.serviceType)}
          <Title level={5} className="!mb-0 text-black font-bold">
            {service.serial || "Service Details"}
          </Title>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-[#fffef5] border-2 border-black transition-colors"
        >
          <X size={20} className="text-black" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Status & Type */}
        <div className="flex items-center justify-between border-2 border-black bg-[#fffff5] p-3">
          <div className="flex items-center gap-2">
            {getServiceTypeIcon(service.serviceType)}
            <Text strong className="text-black font-bold">{capitalize(service.serviceType)}</Text>
          </div>
          {getStatusBadge(service.status)}
        </div>

        {/* Print Waybill Button */}
        {service && booking && (
          <div className="flex justify-center">
            <PrintWaybill
              size="middle"
              selectedServiceTicketsForPrint={[
                {
                  ...service,
                  booking,
                  clientService: service.client || undefined,
                } as TWaybillService,
              ]}
              className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#fffff5] text-black hover:bg-[#fffef5] font-bold uppercase tracking-wide"
            />
          </div>
        )}

        <div className="border-t-2 border-black my-4" />

        {/* Service Details */}
        <div>
          <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
            Service Information
          </Text>
          <div className="border-2 border-black bg-[#fffff5] p-3 space-y-2">
            <div className="flex justify-between items-center">
              <Text className="text-black font-bold text-xs uppercase">Date</Text>
              <div className="flex items-center gap-1">
                <Calendar size={14} className="text-black" />
                <Text className="text-black">
                  {service.serviceDatetime
                    ? formatDate(service.serviceDatetime)
                    : "-"}
                </Text>
              </div>
            </div>
            {service.serviceDatetime && (
              <div className="flex justify-between items-center">
                <Text className="text-black font-bold text-xs uppercase">Time</Text>
                <Text className="text-black">
                  {(() => {
                    const displayTime = getServiceDisplayTime(service.serviceDatetime, service.startDatetime);
                    return displayTime ? displayTime.format("h:mm A") : "-";
                  })()}
                </Text>
              </div>
            )}
            <div className="flex justify-between items-center">
              <Text className="text-black font-bold text-xs uppercase">Booking Ref</Text>
              <Text className="text-black">{booking?.referenceNumber || "-"}</Text>
            </div>
            <div className="flex justify-between items-center">
              <Text className="text-black font-bold text-xs uppercase">Project</Text>
              <Text className="text-black">{booking?.project?.name || "-"}</Text>
            </div>
            {booking?.sidemark && (
              <div className="flex justify-between items-center">
                <Text className="text-black font-bold text-xs uppercase">Sidemark</Text>
                <Text className="text-black">{booking.sidemark}</Text>
              </div>
            )}
          </div>
        </div>

        <div className="border-t-2 border-black my-4" />

        {/* Location */}
        <div>
          <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
            Location
          </Text>
          <div className="mt-2 p-3 border-2 border-black bg-[#fffff5]">
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-black mt-0.5" />
              <div>
                <Text strong className="block text-black">{service.location?.name || "Location"}</Text>
                <Text className="text-sm text-black">{addressDisplay}</Text>
              </div>
            </div>
          </div>
        </div>

        {/* Scope */}
        {service.scope && (
          <>
            <div className="border-t-2 border-black my-4" />
            <div>
              <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
                Scope
              </Text>
              <div className="mt-2 p-3 border-2 border-black bg-[#fffff5]">
                <Text className="text-black">{service.scope}</Text>
              </div>
            </div>
          </>
        )}

        {/* Resources */}
        {(service.vessel || (service.staffs && service.staffs.length > 0) || (service.supplyItems && service.supplyItems.length > 0)) && (
          <>
            <div className="border-t-2 border-black my-4" />
            <div>
              <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
                Resources
              </Text>
              <div className="mt-2 space-y-3">
                {/* Vessel/Truck */}
                {service.vessel && (
                  <div className="p-3 border-2 border-black bg-[#fffff5]">
                    <div className="flex items-center gap-2 mb-1">
                      <Truck size={16} className="text-black" />
                      <Text strong className="text-black font-bold">Truck</Text>
                    </div>
                    <Text className="text-sm text-black">
                      {service.vessel.name}
                      {service.vessel.licensePlate && (
                        <Text className="text-black"> ({service.vessel.licensePlate})</Text>
                      )}
                    </Text>
                  </div>
                )}

                {/* Staff */}
                {service.staffs && service.staffs.length > 0 && (
                  <div className="p-3 border-2 border-black bg-[#fffff5]">
                    <div className="flex items-center gap-2 mb-1">
                      <Users size={16} className="text-black" />
                      <Text strong className="text-black font-bold">Staff</Text>
                    </div>
                    <div className="space-y-1">
                      {service.staffs.map((staff, idx) => (
                        <Text key={idx} className="text-sm block text-black">
                          {staff.user?.firstName && staff.user?.lastName 
                            ? `${staff.user.firstName} ${staff.user.lastName}`
                            : staff.user?.email || 'Unknown'}
                        </Text>
                      ))}
                    </div>
                  </div>
                )}

                {/* Materials/Supplies */}
                {service.supplyItems && service.supplyItems.length > 0 && (
                  <div className="p-3 border-2 border-black bg-[#fffff5]">
                    <div className="flex items-center gap-2 mb-1">
                      <Wrench size={16} className="text-black" />
                      <Text strong className="text-black font-bold">Materials</Text>
                    </div>
                    <div className="space-y-1">
                      {service.supplyItems.map((item, idx) => (
                        <Text key={idx} className="text-sm block text-black">
                          • {item.supply?.name || 'Unknown'}
                          {item.quantity > 1 && (
                            <Text className="text-black">
                              {' '}({item.quantity}{item.supply?.unit ? ` ${item.supply.unit}` : ''})
                            </Text>
                          )}
                        </Text>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Service Client */}
        {service.client && (
          <>
            <div className="border-t-2 border-black my-4" />
            <div>
              <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
                Service Client
              </Text>
              <div className="mt-2 p-3 border-2 border-black bg-[#fffff5]">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-black" />
                  <Text strong className="text-black font-bold">
                    {service.client.companyName ||
                      `${service.client.firstName || ""} ${service.client.lastName || ""}`.trim() ||
                      "-"}
                  </Text>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Inventory Items */}
        {service.inventories && service.inventories.length > 0 && (
          <>
            <div className="border-t-2 border-black my-4" />
            <div>
              <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
                Inventory Items ({service.inventories.length})
              </Text>
              <div className="mt-2 space-y-2">
                {service.inventories.map((item) => (
                  <InventoryExpandableItem
                    key={item.id}
                    item={item}
                    onViewInInventory={(id) => handleViewInInventory(item)}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Notes */}
        {service.notes && (
          <>
            <div className="border-t-2 border-black my-4" />
            <div>
              <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
                Notes
              </Text>
              <div className="mt-2 p-3 border-2 border-black bg-[#fffff5]">
                <Text className="text-black">{service.notes}</Text>
              </div>
            </div>
          </>
        )}

        {/* Completion Info */}
        {service.status === JobStatus.COMPLETED && (service.completed_at || service.completedAt) && (
          <>
            <div className="border-t-2 border-black my-4" />
            <div>
              <Text strong className="text-black text-sm uppercase tracking-wide font-semibold mb-3 block">
                Completion
              </Text>
              <div className="space-y-2">
                <div className="border-2 border-black bg-[#fffff5] p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <Text className="text-black font-bold text-xs uppercase">Completed</Text>
                    <Text className="text-black">{formatDate(service.completed_at || service.completedAt)}</Text>
                  </div>
                  {service.clientSignature && (
                    <div className="flex justify-between items-center">
                      <Text className="text-black font-bold text-xs uppercase">Signature</Text>
                      <span className="inline-block px-2 py-0.5 text-xs font-bold uppercase border-2 border-black bg-black text-white">
                        Received
                      </span>
                    </div>
                  )}
                  {service.addOnServices && service.addOnServices.length > 0 && (
                    <div className="flex justify-between items-center">
                      <Text className="text-black font-bold text-xs uppercase">Completed Tasks</Text>
                      <Text className="text-black">
                        {service.addOnServices.filter(s => s.status === 'completed').length} of {service.addOnServices.length}
                      </Text>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowCompletionDetails(true)}
                  className="w-full mt-2 px-3 py-2 border-2 border-black bg-[#fffff5] text-black hover:bg-[#fffef5] transition-all text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2"
                >
                  <Calendar size={14} />
                  View Completion Details
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      </div>

      {/* Completion Details Sidebar (nested) - appears to the left of service sidebar */}
      {showCompletionDetails && (
        <>
          <div
            className={`absolute right-full top-0 h-full bg-[#fffff5] transition-all duration-300 ease-in-out border-r-2 border-black max-w-[100vw] overflow-x-hidden w-[400px] translate-x-0`}
            style={{ zIndex: 70 }}
          >
            <ServiceCompletionSidebar
              service={service}
              onClose={() => setShowCompletionDetails(false)}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[55] lg:hidden"
            onClick={() => setShowCompletionDetails(false)}
          />
        </>
      )}
    </div>
  );
};
